import React, { useMemo } from 'react';
import { format } from 'date-fns';

import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';

import { useQuery } from '@tanstack/react-query';
import { fetcher } from '@dashboard/services/request';
import StatusBadge from '@dashboard/components/StatusBadge';
import { Button } from '@dashboard/components/ui/button';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import { Alert, AlertTitle, AlertDescription } from '@dashboard/components/ui/alert';
import EmptyState from '@dashboard/components/EmptyState';
import getStripeDashboardUrl from '@dashboard/lib/stripe/get-stripe-dashboard-url';
import { DataGrid } from '@dashboard/components/DataGrid';
import CardHeader from '@dashboard/components/CardHeader';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import Tooltip from '@dashboard/components/Tooltip';
import {
  ArrowTopRightOnSquareIcon,
  ArrowDownTrayIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import PaymentsSubscriptionPaymentRefundDialog from '../components/PaymentsSubscriptionPaymentRefundDialog';

const SUBSCRIPTION_PAYMENT_STATUS_LABELS = {
  succeeded: 'Paid',
  pending: 'Pending',
  failed: 'Failed',
  incomplete: 'Incomplete',
};

function getSubscriptionPaymentStatus(payment) {
  if (payment.refunded) return 'refunded';
  if (payment.partially_refunded) return 'partially_refunded';
  if (payment.paid) return 'paid';

  return payment.paymentStatus;
}

function getSubscriptionPaymentStatusLabel(payment) {
  if (payment.refunded) return 'Refunded';
  if (payment.partially_refunded) return 'Partially refunded';
  if (payment.paid) return 'Paid';

  return SUBSCRIPTION_PAYMENT_STATUS_LABELS[payment.paymentStatus];
}

const PaymentsSubscriptionsDetailPayments = ({ subscription, subscriptionId }) => {
  const { token, seller, isAdminLoggedInAsSeller } = useAuth();

  const {
    data: subscriptionPayments,
    error: subscriptionPaymentsError,
    isLoading: isLoadingSubscriptionPayments,
  } = useQuery({
    queryKey: ['subscriptionPayments', subscriptionId],
    queryFn: () =>
      fetcher(`/sellers/${subscription.seller}/subscriptions/${subscriptionId}/payments`, token),
  });

  const columns = useMemo(
    () => [
      {
        accessorKey: 'amount',
        header: 'Amount',
        cell: ({ row }) =>
          getPriceLabel({
            amount: row.original.amount,
            currency: subscription.currency,
          }),
        enableSorting: false,
      },
      {
        accessorKey: 'createdAt',
        header: 'Date',
        cell: ({ row }) => format(new Date(row.original.createdAt), 'd MMM, yyyy, HH:mm'),
        enableSorting: false,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <StatusBadge
            status={getSubscriptionPaymentStatus(row.original)}
            label={getSubscriptionPaymentStatusLabel(row.original)}
          />
        ),
        enableSorting: false,
      },
      {
        id: 'invoice',
        header: 'Invoice',
        cell: ({ row }) => {
          const payment = row.original;
          return (
            <div className="flex gap-2 items-center">
              <Button asChild variant="outline" size="sm">
                <a href={payment.stripeInvoicePdf} target="_blank" rel="noopener noreferrer">
                  Download
                  <ArrowDownTrayIcon />
                </a>
              </Button>
              {payment.stripeRefundInvoicePdf && (
                <Button asChild variant="outline" size="sm">
                  <a
                    href={payment.stripeRefundInvoicePdf}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Download refund
                    <ArrowDownTrayIcon />
                  </a>
                </Button>
              )}
            </div>
          );
        },
        enableSorting: false,
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const payment = row.original;
          return (
            <div className="flex gap-x-2 justify-end">
              <PaymentsSubscriptionPaymentRefundDialog
                payment={payment}
                subscription={subscription}
              />
              <Tooltip label="View payment in Stripe">
                <Button asChild variant="outline" size="icon-sm">
                  <a
                    href={getStripeDashboardUrl(
                      `/payments/${payment.stripePaymentId || payment.stripePaymentIntentId}`,
                      { seller, isAdminLoggedInAsSeller },
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ArrowTopRightOnSquareIcon />
                  </a>
                </Button>
              </Tooltip>
            </div>
          );
        },
        enableSorting: false,
        meta: { className: 'text-right' },
      },
    ],
    [subscription, seller, isAdminLoggedInAsSeller],
  );

  const payments = subscriptionPayments ?? [];

  return (
    <div className="flex flex-col lg:flex-row mx-auto items-start justify-center">
      <div className="order-2 lg:order-1 w-full lg:pr-8 px-4 pt-2">
        <Card className="mx-auto !px-0 !py-2">
          <CardHeader className="mb-4">
            <CardHeaderTitle>Payments</CardHeaderTitle>
          </CardHeader>

          <CardContent>
            {subscriptionPaymentsError ? (
              <Alert variant="destructive" className="my-4">
                <ExclamationCircleIcon />
                <AlertDescription>Failed to load subscription payments</AlertDescription>
              </Alert>
            ) : (
              <DataGrid
                columns={columns}
                data={payments}
                isLoading={isLoadingSubscriptionPayments}
                emptyState={
                  <EmptyState>
                    <EmptyState.Subtitle>
                      No payments found for this subscription.
                    </EmptyState.Subtitle>
                  </EmptyState>
                }
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentsSubscriptionsDetailPayments;
